import React from 'react';
import styles from './CookiePolicy.module.css';

const CookiePolicy = () => {
  return (
    <div className={styles.cookiePolicyContainer}>
      <main className={styles.cookiePolicyContent}>
        <div className={styles.importantNotice}>
          <h1>Cookie Policy</h1>
          <p className={styles.noticeText}>
            This Cookie Policy explains how we use cookies and similar technologies 
            on our platform to enhance your experience and ensure security.
          </p>
        </div>

        {/* Introduction */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>1</span>
            Introduction to Cookies
          </h2>
          
          <div className={styles.sectionContent}>
            <h3>1.1 What Are Cookies?</h3>
            <p>
              Cookies are small text files that are placed on your device (computer, 
              tablet, or mobile phone) when you visit our platform. They help the 
              platform remember information about your visit, which can make it 
              easier to visit the platform again and make the platform more useful to you.
            </p>

            <h3>1.2 Why We Use Cookies</h3>
            <div className={styles.cookiePurposes}>
              <div className={styles.purposeItem}>
                <h4>Essential Operation</h4>
                <p>Enable core platform functionality and security features</p>
              </div>
              <div className={styles.purposeItem}>
                <h4>User Experience</h4>
                <p>Remember your preferences and improve navigation</p>
              </div>
              <div className={styles.purposeItem}>
                <h4>Security</h4>
                <p>Protect your account and prevent fraudulent activities</p>
              </div>
              <div className={styles.purposeItem}>
                <h4>Analytics</h4>
                <p>Understand how users interact with our platform</p>
              </div>
            </div>
          </div>
        </section>

        {/* Types of Cookies */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>2</span>
            Types of Cookies We Use
          </h2>

          <div className={styles.sectionContent}>
            <h3>2.1 Classification by Purpose</h3>
            
            <div className={styles.cookieTypes}>
              <div className={styles.cookieType}>
                <div className={styles.typeHeader}>
                  <div className={`${styles.typeIndicator} ${styles.essential}`}></div>
                  <h4>Essential Cookies</h4>
                  <span className={styles.cookieTag}>Mandatory</span>
                </div>
                <p>Required for the platform to function properly</p>
                <ul>
                  <li>Session management and user authentication</li>
                  <li>Security and fraud prevention</li>
                  <li>Load balancing across servers</li>
                  <li>Maintaining user preferences</li>
                </ul>
                <p className={styles.cookieNote}>
                  <strong>Cannot be disabled:</strong> These cookies are necessary for 
                  platform operation and cannot be turned off in our systems.
                </p>
              </div>

              <div className={styles.cookieType}>
                <div className={styles.typeHeader}>
                  <div className={`${styles.typeIndicator} ${styles.functional}`}></div>
                  <h4>Functional Cookies</h4>
                  <span className={styles.cookieTag}>Optional</span>
                </div>
                <p>Enhance user experience and remember preferences</p>
                <ul>
                  <li>Language and region preferences</li>
                  <li>Font size and display settings</li>
                  <li>Remembering login details (if enabled)</li>
                  <li>Chatbot interactions and support history</li>
                </ul>
                <p className={styles.cookieNote}>
                  <strong>Can be disabled:</strong> These cookies collect anonymous 
                  information and do not track browsing on other sites.
                </p>
              </div>

              <div className={styles.cookieType}>
                <div className={styles.typeHeader}>
                  <div className={`${styles.typeIndicator} ${styles.analytics}`}></div>
                  <h4>Analytics Cookies</h4>
                  <span className={styles.cookieTag}>Optional</span>
                </div>
                <p>Help us understand how users interact with our platform</p>
                <ul>
                  <li>Visitor counts and traffic sources</li>
                  <li>Most visited pages and features</li>
                  <li>User journey and navigation patterns</li>
                  <li>Error reports and performance metrics</li>
                </ul>
                <p className={styles.cookieNote}>
                  <strong>Can be disabled:</strong> All information collected is 
                  aggregated and anonymized.
                </p>
              </div>
            </div>

            <h3>2.2 Classification by Duration</h3>
            <div className={styles.durationTypes}>
              <div className={styles.durationType}>
                <h5>Session Cookies</h5>
                <p>Temporary cookies that expire when you close your browser</p>
                <span className={styles.durationTag}>Temporary</span>
              </div>
              
              <div className={styles.durationType}>
                <h5>Persistent Cookies</h5>
                <p>Remain on your device until they expire or are deleted</p>
                <span className={styles.durationTag}>Long-term</span>
              </div>
            </div>
          </div>
        </section>

        {/* Specific Cookies Used */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>3</span>
            Specific Cookies on Our Platform
          </h2>

          <div className={styles.sectionContent}>
            <h3>3.1 Essential Security Cookies</h3>
            
            <div className={styles.cookieTable}>
              <div className={styles.tableHeader}>
                <div>Cookie Name</div>
                <div>Purpose</div>
                <div>Duration</div>
                <div>Type</div>
              </div>
              <div className={styles.tableRow}>
                <div><code>session_id</code></div>
                <div>Maintains your login session and authentication state</div>
                <div>Session</div>
                <div><span className={styles.essentialBadge}>Essential</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>csrf_token</code></div>
                <div>Prevents Cross-Site Request Forgery attacks</div>
                <div>Session</div>
                <div><span className={styles.essentialBadge}>Essential</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>security_check</code></div>
                <div>Validates secure connection and device authorization</div>
                <div>24 hours</div>
                <div><span className={styles.essentialBadge}>Essential</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>device_fingerprint</code></div>
                <div>Unique device identification for fraud prevention</div>
                <div>30 days</div>
                <div><span className={styles.essentialBadge}>Essential</span></div>
              </div>
            </div>

            <h3>3.2 Functional & Preference Cookies</h3>
            
            <div className={styles.cookieTable}>
              <div className={styles.tableHeader}>
                <div>Cookie Name</div>
                <div>Purpose</div>
                <div>Duration</div>
                <div>Type</div>
              </div>
              <div className={styles.tableRow}>
                <div><code>user_preferences</code></div>
                <div>Stores language, theme, and display preferences</div>
                <div>90 days</div>
                <div><span className={styles.functionalBadge}>Functional</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>recent_activity</code></div>
                <div>Remembers recently accessed features and pages</div>
                <div>30 days</div>
                <div><span className={styles.functionalBadge}>Functional</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>notification_settings</code></div>
                <div>Stores your notification and alert preferences</div>
                <div>180 days</div>
                <div><span className={styles.functionalBadge}>Functional</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>consent_status</code></div>
                <div>Remembers your cookie consent preferences</div>
                <div>365 days</div>
                <div><span className={styles.functionalBadge}>Functional</span></div>
              </div>
            </div>

            <h3>3.3 Analytics Cookies</h3>
            
            <div className={styles.cookieTable}>
              <div className={styles.tableHeader}>
                <div>Cookie Name</div>
                <div>Purpose</div>
                <div>Duration</div>
                <div>Type</div>
              </div>
              <div className={styles.tableRow}>
                <div><code>_ga</code></div>
                <div>Google Analytics - Distinguishes unique users</div>
                <div>2 years</div>
                <div><span className={styles.analyticsBadge}>Analytics</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>_gid</code></div>
                <div>Google Analytics - Distinguishes users for 24 hours</div>
                <div>24 hours</div>
                <div><span className={styles.analyticsBadge}>Analytics</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>_gat</code></div>
                <div>Google Analytics - Throttles request rate</div>
                <div>1 minute</div>
                <div><span className={styles.analyticsBadge}>Analytics</span></div>
              </div>
              <div className={styles.tableRow}>
                <div><code>platform_performance</code></div>
                <div>Tracks page load times and performance metrics</div>
                <div>Session</div>
                <div><span className={styles.analyticsBadge}>Analytics</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* Third-Party Cookies */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>4</span>
            Third-Party Cookies & Services
          </h2>

          <div className={styles.sectionContent}>
            <h3>4.1 External Services</h3>
            <div className={styles.thirdPartyServices}>
              <div className={styles.serviceItem}>
                <h5>Google Analytics</h5>
                <p>Web analytics service to understand platform usage</p>
                <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                  View Google's Cookie Policy
                </a>
              </div>
              
              <div className={styles.serviceItem}>
                <h5>Payment Gateway Providers</h5>
                <p>Secure payment processing and transaction verification</p>
                <p className={styles.serviceNote}>Cookies used only during payment sessions</p>
              </div>
              
              <div className={styles.serviceItem}>
                <h5>Cloudflare</h5>
                <p>Content delivery network and security protection</p>
                <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className={styles.externalLink}>
                  View Cloudflare's Privacy Policy
                </a>
              </div>
              
              <div className={styles.serviceItem}>
                <h5>Customer Support Chat</h5>
                <p>Live chat support and customer service interactions</p>
                <p className={styles.serviceNote}>Session-based cookies for chat functionality</p>
              </div>
            </div>

            <h3>4.2 Embedded Content</h3>
            <div className={styles.embeddedContent}>
              <p>
                Some pages on our platform may contain embedded content (e.g., videos, 
                documents, social media feeds). Embedded content from other websites 
                behaves in the exact same way as if you visited the other website.
              </p>
              <div className={styles.embeddedNotice}>
                <p><strong>Note:</strong> These websites may collect data about you, 
                use cookies, embed additional third-party tracking, and monitor your 
                interaction with that embedded content.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Cookie Management */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>5</span>
            Managing Your Cookie Preferences
          </h2>

          <div className={styles.sectionContent}>
            <h3>5.1 Browser Controls</h3>
            <div className={styles.managementOptions}>
              <div className={styles.optionItem}>
                <h5>Browser Settings</h5>
                <p>Most web browsers allow you to control cookies through their settings:</p>
                <ul>
                  <li>Delete existing cookies</li>
                  <li>Block future cookies</li>
                  <li>Set preferences for specific websites</li>
                  <li>Enable private browsing modes</li>
                </ul>
              </div>
              
              <div className={styles.optionItem}>
                <h5>Platform Cookie Settings</h5>
                <p>Our platform provides a cookie management dashboard where you can:</p>
                <ul>
                  <li>Review current cookie status</li>
                  <li>Enable/disable optional cookies</li>
                  <li>Set expiration preferences</li>
                  <li>Export your cookie settings</li>
                </ul>
              </div>
            </div>

            <h3>5.2 Browser-Specific Instructions</h3>
            <div className={styles.browserInstructions}>
              <div className={styles.browserItem}>
                <h5>Google Chrome</h5>
                <p>Settings → Privacy and Security → Cookies and other site data</p>
              </div>
              <div className={styles.browserItem}>
                <h5>Mozilla Firefox</h5>
                <p>Options → Privacy & Security → Cookies and Site Data</p>
              </div>
              <div className={styles.browserItem}>
                <h5>Safari</h5>
                <p>Preferences → Privacy → Cookies and website data</p>
              </div>
              <div className={styles.browserItem}>
                <h5>Microsoft Edge</h5>
                <p>Settings → Cookies and site permissions → Cookies and site data</p>
              </div>
            </div>

            <div className={styles.importantNoticeBox}>
              <h4>Important Notice About Essential Cookies</h4>
              <p>
                <strong>Warning:</strong> Disabling essential cookies will prevent our 
                platform from functioning properly. You may not be able to:
              </p>
              <ul>
                <li>Log in to your account</li>
                <li>Initiate or receive loan transactions</li>
                <li>Access secure features of the platform</li>
                <li>Complete verification processes</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Data Protection & Security */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>6</span>
            Data Protection & Security Measures
          </h2>

          <div className={styles.sectionContent}>
            <h3>6.1 Cookie Security Features</h3>
            <div className={styles.securityMeasures}>
              <div className={styles.securityItem}>
                <h5>HTTPS-Only Cookies</h5>
                <p>All cookies are transmitted over encrypted HTTPS connections</p>
              </div>
              <div className={styles.securityItem}>
                <h5>SameSite Attribute</h5>
                <p>Cookies configured to prevent cross-site request forgery attacks</p>
              </div>
              <div className={styles.securityItem}>
                <h5>HttpOnly Flag</h5>
                <p>Critical cookies inaccessible to client-side scripts</p>
              </div>
              <div className={styles.securityItem}>
                <h5>Secure Flag</h5>
                <p>Cookies only transmitted over secure connections</p>
              </div>
            </div>

            <h3>6.2 Data Collected via Cookies</h3>
            <div className={styles.dataCollection}>
              <div className={styles.dataCategory}>
                <h5>Technical Information</h5>
                <ul>
                  <li>Browser type and version</li>
                  <li>Operating system and device type</li>
                  <li>Screen resolution and color depth</li>
                  <li>Network connection type</li>
                </ul>
              </div>
              
              <div className={styles.dataCategory}>
                <h5>Usage Information</h5>
                <ul>
                  <li>Pages visited and time spent</li>
                  <li>Features accessed and interactions</li>
                  <li>Error messages and performance issues</li>
                  <li>Navigation paths and user journeys</li>
                </ul>
              </div>
              
              <div className={styles.dataCategory}>
                <h5>Preference Information</h5>
                <ul>
                  <li>Language and regional settings</li>
                  <li>Display preferences and themes</li>
                  <li>Notification settings</li>
                  <li>Consent management preferences</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Framework */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>7</span>
            Legal Framework & Compliance
          </h2>

          <div className={styles.sectionContent}>
            <h3>7.1 Regulatory Compliance</h3>
            <div className={styles.legalCompliance}>
              <div className={styles.complianceItem}>
                <h5>Information Technology Act, 2000</h5>
                <p>Compliance with data protection and privacy requirements</p>
              </div>
              
              <div className={styles.complianceItem}>
                <h5>RBI Guidelines</h5>
                <p>Adherence to security standards for financial platforms</p>
              </div>
              
              <div className={styles.complianceItem}>
                <h5>GDPR Principles</h5>
                <p>Following privacy by design and user consent principles</p>
              </div>
              
              <div className={styles.complianceItem}>
                <h5>Industry Standards</h5>
                <p>Implementing ISO 27001 security best practices</p>
              </div>
            </div>

            <h3>7.2 User Rights & Consent</h3>
            <div className={styles.userRights}>
              <div className={styles.rightItem}>
                <h5>Right to Information</h5>
                <p>Clear disclosure of all cookies used on our platform</p>
              </div>
              
              <div className={styles.rightItem}>
                <h5>Right to Choice</h5>
                <p>Ability to accept or reject non-essential cookies</p>
              </div>
              
              <div className={styles.rightItem}>
                <h5>Right to Withdraw</h5>
                <p>Option to change cookie preferences at any time</p>
              </div>
              
              <div className={styles.rightItem}>
                <h5>Right to Access</h5>
                <p>Transparent information about data collected</p>
              </div>
            </div>
          </div>
        </section>

        {/* Updates & Contact */}
        <section className={styles.cookieSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>8</span>
            Policy Updates & Contact Information
          </h2>

          <div className={styles.sectionContent}>
            <h3>8.1 Policy Updates</h3>
            <div className={styles.updateInfo}>
              <p>
                We may update this Cookie Policy from time to time to reflect changes 
                in our practices, technologies, legal requirements, or other factors. 
                When we make changes, we will update the "Last Updated" date at the 
                bottom of this page.
              </p>
              <div className={styles.updateProcess}>
                <h5>Update Notification Process</h5>
                <ul>
                  <li>Significant changes will be notified via platform announcement</li>
                  <li>Updated policy will be available 30 days before implementation</li>
                  <li>Users will have opportunity to review and adjust preferences</li>
                  <li>Continued use after changes constitutes acceptance</li>
                </ul>
              </div>
            </div>

            <h3>8.2 Contact for Cookie-Related Queries</h3>
            <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <h5>Data Protection Officer</h5>
                <p>For questions about data protection and cookie usage</p>
                <p className={styles.contactDetail}>Email: support@rupayya.com</p>
              </div>
              
              <div className={styles.contactItem}>
                <h5>Technical Support</h5>
                <p>For assistance with cookie settings and preferences</p>
                <p className={styles.contactDetail}>Help Center: Within platform settings</p>
              </div>
            </div>

            <div className={styles.finalNotice}>
              <p>
                <strong>Important:</strong> By using our platform, you consent to our 
                use of essential cookies. For non-essential cookies, we will seek your 
                explicit consent before they are placed on your device.
              </p>
              <p className={styles.lastUpdated}>
                <strong>Last Updated:</strong> October 25, 2025
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CookiePolicy;